import { Box, Card, Grid, Stack, Typography } from '@mui/material'
import StatusText from 'common/component/StatusText'
import React, { useEffect, useMemo, useState } from 'react'
import defaultProductImage from '../../../assets/images/placeholderproduct.svg';
import colors from 'assets/theme/base/colors';
import { getStaticLegal } from '../../../redux/slices/common';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import Loader from 'common/Loader';
import NoDataFound from 'common/component/NoDataFound';

const LabTestingStandardsDetail = ({ legalId, fullRoleTabSlug }) => {
  const { grey } = colors;
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams();
  const fullRoleTab = useMemo(() => searchParams.get('roleTab'), [searchParams]);
  const { singleLegal, isLoading } = useSelector((state => state.common));
  const [nodataFound, setNodataFound] = useState(false)
  const fullRoleTabheading = useMemo(() => searchParams.get('heading'), [searchParams]);

  useEffect(() => {
    if (fullRoleTabheading) {
      dispatch(getStaticLegal({ id: fullRoleTabheading, platform: fullRoleTab })).unwrap().then(() => {
        setNodataFound(false)
      }).catch(() => {
        setNodataFound(true)
      });
    }
  }, [fullRoleTabheading, fullRoleTab])

  const renderSectionItems = (items) => (
    <Stack spacing={3}>
      {items?.map((item, idx) => (
        <Stack key={item._id || idx} direction="column" spacing={1} alignItems="start">
          <Box display="flex" flexDirection="row" gap={2} alignItems="center">
            {item.image?.url && (
              <Box component="img" src={item.image.url} alt={item.image?.alt || item.title}
                sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
              />
            )}
            <Typography variant='h6' fontWeight="600">{item.title || "-"}</Typography>
          </Box>
          <Box variant="body2" color={grey[600]} dangerouslySetInnerHTML={{ __html: item.description || "" }} />
        </Stack>
      ))}
    </Stack>
  )

  return (
    <>
      {isLoading ? <Loader /> : <>
        {nodataFound ? <NoDataFound /> :
          <Box p={4} pt={2} className="static-pages-container">

            {/**************** Page Title ****************/}
            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
                <Typography variant='h4' fontWeight="semibold" style={{ color: "#2E2E3A" }}>{fullRoleTabheading}</Typography>
                <StatusText status={singleLegal?.status ? "active" : "inactive"} />
              </Stack>
            </Card>

            {/***************** Visible On ****************/}
            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Typography variant='h5' fontWeight="semibold" mb={2} mt={1}>Visible On</Typography>
              <Stack direction="row" alignItems="center" spacing={3}>
                {singleLegal?.displaySettings?.footer && (
                  <badge style={{ backgroundColor: "rgba(161, 98, 247, 0.20)", color: "#2E2E3A", padding: "7px 16px", borderRadius: "100px", fontSize: "16px", fontWeight: "500" }}>Footer</badge>
                )}
                {singleLegal?.displaySettings?.hamburgerMenu && (
                  <badge style={{ backgroundColor: "rgba(161, 98, 247, 0.20)", color: "#2E2E3A", padding: "7px 16px", borderRadius: "100px", fontSize: "16px", fontWeight: "500" }}>Hamburger Menu</badge>
                )}
              </Stack>
            </Card>

            {/***************** Meta Properties ****************/}
            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Typography variant='h5' fontWeight="semibold" mb={3}>Meta Properties</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant='body2' color={grey[600]} mb={1}>Meta Title</Typography>
                  <Typography variant='h5' fontWeight="medium">{singleLegal?.metaProperties?.title || "-"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant='body2' color={grey[600]} mb={1}>Description</Typography>
                  <Typography variant='h5' fontWeight="medium">{singleLegal?.metaProperties?.description || "-"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant='body2' color={grey[600]} mb={1}>Updated By</Typography>
                  <Typography variant='h5' fontWeight="medium">{singleLegal?.createdBy || "-"}</Typography>
                </Grid>
              </Grid>
            </Card>

            {/***************** Header Section ****************/}
            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={3}>
                <Typography variant='h5' fontWeight="semibold">Header Section</Typography>
                <StatusText status={singleLegal?.headerSection?.status ? "active" : "inactive"} />
              </Stack>
              {singleLegal?.headerSection?.status &&
                <Grid container direction="row" justifyContent="center" alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant='body2' color={grey[600]} mb={1}>Title(H1)</Typography>
                        <Typography variant='h5' fontWeight="medium">{singleLegal?.headerSection?.pageTitle || "-"}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant='body2' color={grey[600]} mb={1}>Description</Typography>
                        <Typography variant="h5" fontWeight="medium"
                          dangerouslySetInnerHTML={{ __html: singleLegal?.headerSection?.section?.description || "-" }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant='body2' color={grey[600]} mb={1}>Top Banner</Typography>
                        <img style={{ borderRadius: '20px', width: '100%', height: '320px', objectFit: 'cover' }}
                          src={singleLegal?.headerSection?.section?.image?.url || defaultProductImage} alt="No Image Found" />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              }
            </Card>

            {/***************** What happens if an item is missing or incorrect? ****************/}
            {singleLegal?.itemSection?.status &&
              <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={3}>
                  <Typography variant='h5' fontWeight="semibold">What happens if an item is missing or incorrect?</Typography>
                  <StatusText status={singleLegal?.itemSection?.status ? "active" : "inactive"} />
                </Stack>
                {singleLegal?.itemSection?.pageTitle && (
                  <Typography variant='h4' fontWeight="semibold" mb={3}>{singleLegal.itemSection.pageTitle}</Typography>
                )}
                {renderSectionItems(singleLegal?.itemSection?.section)}
              </Card>
            }

            {/***************** What is a COA? ****************/}
            {singleLegal?.coaSection?.status &&
              <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={3}>
                  <Typography variant='h5' fontWeight="semibold">What is a COA?</Typography>
                  <StatusText status={singleLegal?.coaSection?.status ? "active" : "inactive"} />
                </Stack>
                {singleLegal?.coaSection?.title && (
                  <Typography variant='h4' fontWeight="semibold" mb={1}>{singleLegal.coaSection.title}</Typography>
                )}
                {singleLegal?.coaSection?.description && (
                  <Typography variant="body2" color={grey[600]} mb={3}
                    dangerouslySetInnerHTML={{ __html: singleLegal.coaSection.description }}
                  />
                )}
                <Stack spacing={3}>
                  {singleLegal?.coaSection?.section?.map((item, idx) => (
                    <Card key={item._id || idx} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #ededed', boxShadow: 'none' }}>
                      <Typography variant='h6' fontWeight="600" mb={0.5}>{item.title || "-"}</Typography>
                      {item.description && (
                        <Typography variant="body2" color={grey[600]} mb={2}
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                      {item.section?.length > 0 && (
                        <Grid container spacing={2}>
                          {item.section.map((sub, subIdx) => (
                            <Grid item xs={6} sm={4} md={3} key={sub._id || subIdx}>
                              <Stack spacing={1} alignItems="center">
                                {sub.image && (
                                  <Box component="img" src={sub.image} alt={sub.title}
                                    sx={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 1 }}
                                  />
                                )}
                                {sub.title && (
                                  <Typography variant="body2" fontWeight="medium" textAlign="center">{sub.title}</Typography>
                                )}
                              </Stack>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Card>
                  ))}
                </Stack>
              </Card>
            }

            {/***************** How Hyperwolf reviews product quality? ****************/}
            {singleLegal?.productQualitySection?.status &&
              <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={3}>
                  <Typography variant='h5' fontWeight="semibold">How Hyperwolf reviews product quality?</Typography>
                  <StatusText status={singleLegal?.productQualitySection?.status ? "active" : "inactive"} />
                </Stack>
                {singleLegal?.productQualitySection?.pageTitle && (
                  <Typography variant='h4' fontWeight="semibold" mb={1}>{singleLegal.productQualitySection.pageTitle}</Typography>
                )}
                {singleLegal?.productQualitySection?.description && (
                  <Typography variant="body2" color={grey[600]} mb={3}>{singleLegal.productQualitySection.description}</Typography>
                )}
                <Stack spacing={3}>
                  {singleLegal?.productQualitySection?.section?.map((item, idx) => (
                    <Stack key={item._id || idx} direction="column" spacing={1} alignItems="start">
                      <Box display="flex" flexDirection="row" gap={2} alignItems="center">
                        {item.step && (
                          <Typography variant='body2' fontWeight="600" color="primary">{item.step}</Typography>
                        )}
                        {item.image?.url && (
                          <Box component="img" src={item.image.url} alt={item.image?.alt || item.title}
                            sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
                          />
                        )}
                        <Typography variant='h6' fontWeight="600">{item.title || "-"}</Typography>
                      </Box>
                      <Box variant="body2" color={grey[600]} dangerouslySetInnerHTML={{ __html: item.description || "" }} />
                    </Stack>
                  ))}
                </Stack>
              </Card>
            }

            {/***************** How to get help fast ****************/}
            {singleLegal?.helpFastSection?.status &&
              <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={3}>
                  <Typography variant='h5' fontWeight="semibold">How to get help fast</Typography>
                  <StatusText status={singleLegal?.helpFastSection?.status ? "active" : "inactive"} />
                </Stack>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='body2' color={grey[600]} mb={1}>Heading</Typography>
                    <Typography variant='h5' fontWeight="medium">{singleLegal?.helpFastSection?.heading || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='body2' color={grey[600]} mb={1}>Title</Typography>
                    <Typography variant='h5' fontWeight="medium">{singleLegal?.helpFastSection?.title || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2' color={grey[600]} mb={1}>Description</Typography>
                    <Typography variant="h5" fontWeight="medium"
                      dangerouslySetInnerHTML={{ __html: singleLegal?.helpFastSection?.description || "-" }}
                    />
                  </Grid>
                </Grid>
              </Card>
            }

            {/***************** Link Section ****************/}
            {singleLegal?.linkSection?.status &&
              <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={4}>
                  <Typography variant='h5' fontWeight="semibold">Link Section</Typography>
                  <StatusText status={singleLegal?.linkSection?.status ? "active" : "inactive"} />
                </Stack>
                <Stack spacing={3}>
                  {singleLegal?.linkSection?.section?.map((item, idx) => (
                    <Card key={item._id || idx} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #ededed', boxShadow: 'none' }}>
                      <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                        {item.icon && (
                          <Box component="img" src={item.icon} alt="link icon"
                            sx={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
                          />
                        )}
                        <Typography variant='h6' fontWeight="600">{item.title || "-"}</Typography>
                      </Stack>
                      <Typography variant="body2" color={grey[600]} mb={1}
                        dangerouslySetInnerHTML={{ __html: item.description || "" }}
                      />
                      {item.url && (
                        <Typography variant='body2' color="primary">
                          <a href={item.url} target="_blank" rel="noopener noreferrer">{item.url}</a>
                        </Typography>
                      )}
                    </Card>
                  ))}
                </Stack>
              </Card>
            }

            {/***************** FAQ ****************/}
            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={4}>
                <Typography variant='h5' fontWeight="semibold">FAQs</Typography>
                <StatusText status={singleLegal?.faq?.status ? "active" : "inactive"} />
              </Stack>
              {singleLegal?.faq?.status && <>
                {singleLegal?.faq?.section?.length !== 0 ?
                  <Grid container alignItems="center">
                    {singleLegal?.faq?.section?.map((data, idx) => (
                      <Grid item xs={12} mb={2} key={data._id || idx}>
                        <Typography variant="h5" fontWeight="medium"
                          dangerouslySetInnerHTML={{ __html: data?.question || "-" }}
                        />
                        <Typography variant="body2" color={grey[600]}
                          dangerouslySetInnerHTML={{ __html: data?.answer || "-" }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  : <NoDataFound />
                }
              </>}
            </Card>

          </Box>
        }
      </>}
    </>
  )
}

export default LabTestingStandardsDetail
